import React, { useEffect, useState } from 'react';
import styles from '../../assets/styles/OurCourses/OurCourses.module.css';
import { useNavigate } from 'react-router-dom';
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const API_BASE   = `${ENV_API_BASE_URL}/api`;
const ASSET_BASE = `${ENV_API_BASE_URL}`;

const toAssetUrl = (value) => {
  if (!value) return '';
  if (typeof value !== 'string') return value;
  if (value.startsWith('http') || value.startsWith('data:')) return value;
  return `${ASSET_BASE}/${value.replace(/^\/+/, '')}`;
};

// Static fallback used when no API data is available
const FALLBACK_COURSES = [
  {
    _id: 'f1',
    title: "100 Hour Teachers Training Course",
    description: "Be a part of USA Yoga Alliance registered 100 Hour Yoga Teacher Training with Hatha Yogashram, Rishikesh, India. This foundational course is ideal for beginners who want to explore yogic philosophy, asana practice, pranayama, and meditation while building a strong base for a yogic lifestyle.",
    card: { image: "https://images.unsplash.com/photo-1588286840104-8957b019727f?w=800&q=80", link: "/course/100-hour-yttc" },
  },
  {
    _id: 'f2',
    title: "200 Hour Teachers Training Course",
    description: "The 200 Hour Yoga Teacher Training Course is a complete and immersive program registered with USA Yoga Alliance (RYT 200). This course is designed to deepen your understanding of yoga through asana alignment, pranayama, meditation, anatomy, philosophy, and teaching methodology.",
    card: { image: "https://images.unsplash.com/photo-1599447421416-3414500d18a5?w=800&q=80", link: "/course/200-hour-yttc" },
  },
  {
    _id: 'f3',
    title: "300 Hour Teachers Training Course",
    description: "The 300 Hour Yoga Teacher Training Course is an advanced-level program for students who have completed 200 hours of training. It focuses on refined teaching skills, advanced asana practices, in-depth yoga philosophy, therapeutic applications, and self-transformation.",
    card: { image: "https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=800&q=80", link: "/course/300-hour-yttc" },
  },
  {
    _id: 'f4',
    title: "500 Hour Teachers Training Course",
    description: "The 500 Hour Yoga Teacher Training Course is a master-level program registered with USA Yoga Alliance (RYT 500). This intensive course combines both 200 and 300 hour teachings, offering complete mastery in traditional yoga practices, advanced philosophy, anatomy, and professional teaching excellence.",
    card: { image: "https://images.unsplash.com/photo-1603988363607-e1e4a66962c6?w=800&q=80", link: "/course/500-hour-yttc" },
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Props (all optional):
//   courseId    – current course _id  → used to fetch related courses
//   category    – current course category → fallback if courseId not provided
//   sectionData – { tag, title, description } from course.ourCoursesSection
//   excludeSlug – slug of current page so it is never shown as a related card
// ─────────────────────────────────────────────────────────────────────────────
const OurCourses = ({ courseId, category, sectionData, excludeSlug }) => {
  const navigate  = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Section heading values ─────────────────────────────────────────────────
  const tag         = sectionData?.tag         || 'Our Courses';
  const title       = sectionData?.title       || 'Our Courses';
  const description = sectionData?.description || 'The homeland of Yoga, Rishikesh from the ancient era attracts yogis to conduct yoga activities in the lap of nature. The pious land of Rishikesh with the spiritual existence of the Holy River Ganga and the natural environment created by the majestic Himalayan Range is the place where you can learn and feel yoga from the bottom of your heart.';
  const subtitle    = 'So are you ready to embark your Yogic Journey in Rishikesh with Hathayogashram?';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let data = [];

        if (courseId) {
          // ── Related endpoint: same category, excludes current course ──────
          const res = await fetch(`${API_BASE}/courses/${courseId}/related?limit=4`);
          const json = await res.json();
          data = json?.data || [];
        } else if (category) {
          // ── Fallback: fetch by category ───────────────────────────────────
          const res = await fetch(`${API_BASE}/courses?category=${category}&limit=4`);
          const json = await res.json();
          data = (json?.data || []).filter((c) => c.slug !== excludeSlug);
        } else {
          // ── No context: fetch teacher-training courses ────────────────────
          const res = await fetch(`${API_BASE}/courses?category=teacher-training&limit=4`);
          const json = await res.json();
          data = json?.data || [];
        }

        setCourses(data.length ? data : FALLBACK_COURSES);
      } catch {
        setCourses(FALLBACK_COURSES);
      }
      setLoading(false);
    };

    load();
  }, [courseId, category, excludeSlug]);

  // ── Derive display values from a course object ────────────────────────────
  const getImage = (c) =>
    toAssetUrl(c.card?.image || c.teacherTraining?.images?.[0] || '');

  const getLink = (c) =>
    c.card?.link || c.teacherTraining?.path || `/course/${c.slug}`;

  const getDescription = (c) =>
    c.description || c.card?.description || '';

  return (
    <section className={styles.coursesSection}>
      <div className={styles.coursesContainer}>

        <h1 className={styles.coursesTitle}>{title}</h1>

        {description && (
          <p className={styles.coursesIntro}>{description}</p>
        )}

        <p className={styles.coursesSubtitle}>{subtitle}</p>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', opacity: 0.6 }}>
            Loading courses…
          </div>
        ) : (
          <div className={styles.coursesGrid}>
            {courses.map((course) => {
              const img  = getImage(course);
              const link = getLink(course);
              const desc = getDescription(course);

              return (
                <div key={course._id} className={styles.courseCard}>
                  <div className={styles.courseImageWrapper}>
                    {img ? (
                      <img
                        src={img}
                        alt={course.title}
                        className={styles.courseImage}
                      />
                    ) : (
                      <div
                        className={styles.courseImage}
                        style={{
                          background: '#f0ebe3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '48px',
                        }}
                      >
                        🧘
                      </div>
                    )}
                    <div className={styles.courseOverlay}>
                      <h2 className={styles.courseTitle}>{course.title}</h2>
                    </div>
                  </div>

                  <div className={styles.courseContent}>
                    {desc && (
                      <p className={styles.courseDescription}>{desc}</p>
                    )}
                    <button
                      className={styles.courseBtn}
                      onClick={() => navigate(link)}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default OurCourses;